<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useNotificationCenterStore } from '@/stores/notificationCenter'
import type { CenterNotification } from '@/stores/notificationCenter'
import {
  Bell, BellOff, Check, CheckCheck, Clock,
  Ticket as TicketIcon, MessageCircle, Shield, Info, X, Trash2
} from 'lucide-vue-next'

const store = useNotificationCenterStore()
const router = useRouter()
const snoozeMenuId = ref<string | null>(null)

const typeIconMap: Record<CenterNotification['type'], typeof TicketIcon> = {
  ticket: TicketIcon,
  chat: MessageCircle,
  sla: Clock,
  system: Shield,
  info: Info,
}

const typeColorMap: Record<CenterNotification['type'], string> = {
  ticket: 'text-primary-400',
  chat: 'text-success-400',
  sla: 'text-amber-400',
  system: 'text-danger-400',
  info: 'text-violet-400',
}

const snoozeOptions = [
  { label: '15 minutes', ms: 15 * 60 * 1000 },
  { label: '1 hour', ms: 60 * 60 * 1000 },
  { label: '4 hours', ms: 4 * 60 * 60 * 1000 },
  { label: 'Tomorrow', ms: 24 * 60 * 60 * 1000 },
]

function relativeTime(timestamp: number): string {
  const diff = Date.now() - timestamp
  const minutes = Math.floor(diff / 60000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

function handleClick(n: CenterNotification) {
  store.markRead(n.id)
  if (n.actionRoute) {
    router.push(n.actionRoute)
  }
  store.close()
}

function handleSnooze(id: string, ms: number) {
  store.snooze(id, ms)
  snoozeMenuId.value = null
}

function toggleSnoozeMenu(id: string) {
  snoozeMenuId.value = snoozeMenuId.value === id ? null : id
}
</script>

<template>
  <!-- Backdrop -->
  <Transition
    enter-active-class="transition-opacity duration-200"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="transition-opacity duration-200"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="store.isOpen"
      class="fixed inset-0 bg-black/40 z-40"
      @click="store.close()"
    />
  </Transition>

  <!-- Slide-out Panel -->
  <Transition
    enter-active-class="transition-transform duration-300 ease-out"
    enter-from-class="translate-x-full"
    enter-to-class="translate-x-0"
    leave-active-class="transition-transform duration-200 ease-in"
    leave-from-class="translate-x-0"
    leave-to-class="translate-x-full"
  >
    <div
      v-if="store.isOpen"
      class="fixed top-0 right-0 h-full w-[380px] max-w-full bg-surface-900 border-l border-surface-800 z-40 flex flex-col shadow-2xl"
    >
      <!-- Header -->
      <div class="flex items-center justify-between px-4 py-3 border-b border-surface-800">
        <div class="flex items-center gap-2">
          <Bell class="w-5 h-5 text-primary-400" />
          <h2 class="text-lg font-semibold text-surface-100">Notifications</h2>
          <span
            v-if="store.unreadCount > 0"
            class="flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold bg-primary-500 text-white rounded-full"
          >
            {{ store.unreadCount }}
          </span>
        </div>
        <div class="flex items-center gap-1">
          <button
            v-if="store.unreadCount > 0"
            @click="store.markAllRead()"
            class="p-1.5 text-surface-400 hover:text-primary-400 hover:bg-surface-800 rounded-lg transition-colors"
            title="Mark all read"
          >
            <CheckCheck class="w-4 h-4" />
          </button>
          <button
            v-if="store.notifications.length > 0"
            @click="store.clearAll()"
            class="p-1.5 text-surface-400 hover:text-danger-400 hover:bg-surface-800 rounded-lg transition-colors"
            title="Clear all"
          >
            <Trash2 class="w-4 h-4" />
          </button>
          <button
            @click="store.close()"
            class="p-1.5 text-surface-400 hover:text-surface-100 hover:bg-surface-800 rounded-lg transition-colors"
            title="Close"
          >
            <X class="w-4 h-4" />
          </button>
        </div>
      </div>

      <!-- Notification List -->
      <div class="flex-1 overflow-y-auto">
        <!-- Empty state -->
        <div v-if="store.activeNotifications.length === 0" class="flex flex-col items-center justify-center h-full text-center px-6">
          <BellOff class="w-12 h-12 text-surface-700 mb-3" />
          <p class="text-surface-400 text-sm">No notifications</p>
          <p class="text-surface-600 text-xs mt-1">You're all caught up</p>
        </div>

        <!-- Grouped notifications -->
        <div v-else>
          <div v-for="group in store.grouped" :key="group.label" class="py-1">
            <div class="px-4 py-2 text-xs font-semibold text-surface-500 uppercase tracking-wider">
              {{ group.label }}
            </div>
            <div
              v-for="n in group.items"
              :key="n.id"
              class="group relative px-4 py-3 hover:bg-surface-850 cursor-pointer transition-colors border-b border-surface-800/50"
              @click="handleClick(n)"
            >
              <div class="flex items-start gap-3">
                <!-- Type icon -->
                <div class="flex-shrink-0 mt-0.5">
                  <component
                    :is="typeIconMap[n.type]"
                    class="w-5 h-5"
                    :class="typeColorMap[n.type]"
                  />
                </div>

                <!-- Content -->
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2">
                    <p class="text-sm font-medium truncate" :class="n.read ? 'text-surface-400' : 'text-surface-100'">
                      {{ n.title }}
                    </p>
                    <!-- Unread dot -->
                    <span v-if="!n.read" class="flex-shrink-0 w-2 h-2 bg-primary-400 rounded-full" />
                  </div>
                  <p class="text-xs mt-0.5 line-clamp-2" :class="n.read ? 'text-surface-500' : 'text-surface-400'">
                    {{ n.body }}
                  </p>
                  <p class="text-xs text-surface-600 mt-1">{{ relativeTime(n.timestamp) }}</p>
                </div>

                <!-- Actions (visible on hover) -->
                <div class="flex-shrink-0 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <!-- Snooze button -->
                  <div class="relative">
                    <button
                      @click.stop="toggleSnoozeMenu(n.id)"
                      class="p-1 text-surface-500 hover:text-amber-400 hover:bg-surface-800 rounded transition-colors"
                      title="Snooze"
                    >
                      <Clock class="w-3.5 h-3.5" />
                    </button>
                    <!-- Snooze dropdown -->
                    <div
                      v-if="snoozeMenuId === n.id"
                      class="absolute right-0 top-full mt-1 w-36 bg-surface-800 border border-surface-700 rounded-lg shadow-xl z-50 py-1"
                    >
                      <button
                        v-for="opt in snoozeOptions"
                        :key="opt.label"
                        @click.stop="handleSnooze(n.id, opt.ms)"
                        class="w-full text-left px-3 py-1.5 text-xs text-surface-300 hover:bg-surface-700 hover:text-surface-100 transition-colors"
                      >
                        {{ opt.label }}
                      </button>
                    </div>
                  </div>
                  <!-- Mark read -->
                  <button
                    v-if="!n.read"
                    @click.stop="store.markRead(n.id)"
                    class="p-1 text-surface-500 hover:text-success-400 hover:bg-surface-800 rounded transition-colors"
                    title="Mark as read"
                  >
                    <Check class="w-3.5 h-3.5" />
                  </button>
                  <!-- Dismiss -->
                  <button
                    @click.stop="store.dismiss(n.id)"
                    class="p-1 text-surface-500 hover:text-danger-400 hover:bg-surface-800 rounded transition-colors"
                    title="Dismiss"
                  >
                    <X class="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>
